/**
 * L'écran Absences — ce qui empêche une ressource de rouler, et pourquoi.
 *
 * Un seul écran pour les trois parcs : un chauffeur en congés et une remorque
 * en carrosserie sont la même chose vue de l'exploitation — quelque chose qui
 * ne roulera pas, sur une période. C'est ce que l'utilisateur a demandé le
 * 03/09/2026 : « la gestion des absences (personnel et véhicules) ».
 *
 * **LES MOTIFS SONT PARAMÉTRÉS PAR LA SOCIÉTÉ**, et c'est pourquoi cet écran en
 * porte le réglage. Stafa est vendu à d'autres transporteurs : un frigorifique
 * aura « défaut de groupe froid », un déménageur autre chose. Sans ce panneau,
 * la table serait paramétrable en théorie et vide en fait — et l'écran
 * inutilisable le premier jour, puisqu'aucun motif n'existe au départ.
 *
 * **LA FAMILLE, ELLE, N'EST PAS UN PARAMÈTRE.** Deux valeurs, pas une de plus :
 * personne ne choisit qu'un camion parte en congés. Elle se pose à la création
 * d'un motif et ne se modifie plus — la faire passer du matériel au personnel
 * rendrait fausses les indisponibilités déjà écrites.
 *
 * Découpe habituelle : `VueAbsences` dessine, `EcranAbsences` tient l'état.
 * Le calcul est dans `indisponibilite.ts`, en fonctions pures.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  changerArret,
  changerMotif,
  creerMotif,
  lireAffectations,
  lireIndisponibilites,
  lireMotifs,
  lireParc,
  poserArret,
  supprimerArret,
  type EchecApi,
} from './api.js';
import { aujourdhui, enFrancais, veille, type Affectation } from './affectation.js';
import {
  attelageDit,
  attelagesGenants,
  commandes,
  decompte,
  familleDe,
  genreDit,
  moment,
  motifsDe,
  pourCreation,
  quand,
  ranger,
  saisieVide,
  validerArret,
  type Famille,
  type Genre,
  type Indisponibilite,
  type Motif,
  type SaisieArret,
} from './indisponibilite.js';
import { nommer, type LigneParc } from './parc.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';

type EtatBase =
  | { readonly nom: 'chargement' }
  | {
      readonly nom: 'pret';
      readonly chauffeurs: readonly LigneParc[];
      readonly vehicules: readonly LigneParc[];
      readonly remorques: readonly LigneParc[];
      readonly arrets: readonly Indisponibilite[];
      readonly motifs: readonly Motif[];
      readonly affectations: readonly Affectation[];
    }
  | { readonly nom: 'panne' };

type Edition =
  | { readonly nom: 'aucune' }
  | {
      readonly nom: 'creation';
      readonly saisie: SaisieArret;
      readonly fautes: Readonly<Record<string, string>>;
    }
  | { readonly nom: 'motifs' };

const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cet arrêt n’existe plus. Il a pu être supprimé entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  // Les deux cas étaient dits par une seule phrase, faute d'une cause distincte
  // pour le recouvrement — corrigé le 04/09/2026 : chacun a désormais la sienne.
  invalide:
    'Le motif ne correspond pas à cette ressource, ou une date n’est pas acceptée.',
  conflit: 'Cette ressource est déjà indisponible sur tout ou partie de cette période.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache:
    'Ce motif est employé par des arrêts déjà enregistrés : désactivez-le plutôt que de le supprimer.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

const GENRES: readonly Genre[] = ['chauffeur', 'vehicule', 'remorque'];

/** Le parc dont relève un genre — pour nommer ses lignes. */
function parcDe(genre: Genre) {
  if (genre === 'chauffeur') return PARC_CHAUFFEURS;
  if (genre === 'vehicule') return PARC_VEHICULES;
  return PARC_REMORQUES;
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ProprietesVueAbsences {
  readonly etat: EtatBase;
  readonly jour: string;
  readonly quandJour: (jour: string) => void;
  readonly edition: Edition;
  readonly quandCreer: () => void;
  readonly quandMotifs: () => void;
  readonly quandFermer: () => void;
  readonly quandChamp: (champ: keyof SaisieArret, valeur: string) => void;
  readonly quandEnregistrer: () => void;
  readonly quandClore: (id: string) => void;
  readonly quandRouvrir: (id: string) => void;
  /** L'arrêt dont la suppression est proposée, ou `null`. */
  readonly aSupprimer: string | null;
  readonly quandDemanderSuppression: (id: string) => void;
  readonly quandSupprimer: () => void;
  readonly quandRenoncer: () => void;
  readonly quandCreerMotif: (nom: string, famille: Famille) => void;
  readonly quandBasculerMotif: (id: string, actif: boolean) => void;
  readonly envoi: boolean;
  readonly refus: string | null;
}

export function VueAbsences({
  etat,
  jour,
  quandJour,
  edition,
  quandCreer,
  quandMotifs,
  quandFermer,
  quandChamp,
  quandEnregistrer,
  quandClore,
  quandRouvrir,
  aSupprimer,
  quandDemanderSuppression,
  quandSupprimer,
  quandRenoncer,
  quandCreerMotif,
  quandBasculerMotif,
  envoi,
  refus,
}: ProprietesVueAbsences) {
  if (etat.nom === 'chargement') {
    return (
      <div className="corps">
        <section className="liste">
          <p className="attente">Chargement des arrêts…</p>
        </section>
      </div>
    );
  }

  if (etat.nom === 'panne') {
    return (
      <div className="corps">
        <section className="liste">
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        </section>
      </div>
    );
  }

  const ranges = ranger(etat.arrets, jour);
  const ouvert = edition.nom !== 'aucune';

  return (
    <div className={ouvert ? 'corps ouvert' : 'corps'}>
      <section className="liste">
        <div className="barre-outils barre-affectation">
          <label className="champ-date">
            <span className="etiquette">
              Au <span className="sourdine">· la date où l’on regarde</span>
            </span>
            <input
              className="champ"
              type="date"
              value={jour}
              disabled={envoi}
              onChange={(e) => quandJour(e.target.value)}
            />
          </label>
          <div className="bascule-vue">
            <button className="bouton" type="button" onClick={quandCreer}>
              Déclarer un arrêt
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={quandMotifs}
            >
              Motifs
            </button>
          </div>
        </div>

        {refus !== null && <p className="bandeau">{refus}</p>}

        {etat.motifs.length === 0 && (
          <p className="empechement">
            Aucun motif n’est encore réglé. Chaque société pose les siens — commencez
            par « Motifs ».
          </p>
        )}

        {ranges.length > 0 && <p className="decompte">{decompte(ranges, jour)}</p>}

        {ranges.length === 0 ? (
          <div className="vide">
            <p>Aucun arrêt n’est enregistré.</p>
            <button className="bouton" type="button" onClick={quandCreer}>
              Déclarer un arrêt
            </button>
          </div>
        ) : (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Ressource</th>
                  <th scope="col" className="col-large">
                    Genre
                  </th>
                  <th scope="col">Motif</th>
                  {/* `col-large` : mesuré le 03/09/2026, panneau ouvert, le
                      tableau débordait de son cadre et coupait les commandes
                      en plein mot. « du 01/09/2026 au 10/09/2026 » est la
                      colonne la plus large et la moins utile quand on est en
                      train de saisir. */}
                  <th scope="col" className="col-large">
                    Période
                  </th>
                  <th scope="col" className="col-actions">
                    <span className="sourdine">—</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ranges.map((arret) => {
                  const ou = moment(arret, jour);
                  return (
                    <tr
                      className={ou === 'passe' ? 'rangee eteinte' : 'rangee'}
                      key={arret.id}
                    >
                      <td className="col-souple">{arret.ressource}</td>
                      <td className="sourdine col-large">{genreDit(arret.genre)}</td>
                      <td>
                        {arret.motif}
                        {ou === 'en_cours' && (
                          <span className="pastille">en cours</span>
                        )}
                        {ou === 'a_venir' && <span className="pastille">à venir</span>}
                      </td>
                      <td className="col-large">{quand(arret, jour)}</td>
                      <td className="col-actions">
                        <div className="commandes">
                          {aSupprimer === arret.id ? (
                            <>
                              <button
                                className="bouton bouton-risque"
                                type="button"
                                disabled={envoi}
                                onClick={quandSupprimer}
                              >
                                {envoi ? 'Suppression…' : 'Oui, supprimer'}
                              </button>
                              <button
                                className="bouton bouton-discret"
                                type="button"
                                disabled={envoi}
                                onClick={quandRenoncer}
                              >
                                Annuler
                              </button>
                            </>
                          ) : (
                            <>
                              {commandes(arret, jour).clore && (
                                <button
                                  className="bouton bouton-discret"
                                  type="button"
                                  disabled={envoi}
                                  onClick={() => quandClore(arret.id)}
                                  title={`Fin posée au ${enFrancais(veille(jour)) ?? ''}`}
                                >
                                  Clore
                                </button>
                              )}
                              {commandes(arret, jour).retirerLaFin !== false && (
                                <button
                                  className="bouton bouton-discret"
                                  type="button"
                                  disabled={envoi}
                                  onClick={() => quandRouvrir(arret.id)}
                                >
                                  {commandes(arret, jour).retirerLaFin}
                                </button>
                              )}
                              <button
                                className="bouton bouton-risque"
                                type="button"
                                disabled={envoi}
                                onClick={() => quandDemanderSuppression(arret.id)}
                                title="Pour une saisie fautive seulement"
                              >
                                Supprimer
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {edition.nom === 'creation' && (
        <FormulaireArret
          saisie={edition.saisie}
          fautes={edition.fautes}
          ressources={
            edition.saisie.genre === 'chauffeur'
              ? etat.chauffeurs
              : edition.saisie.genre === 'vehicule'
                ? etat.vehicules
                : etat.remorques
          }
          motifs={motifsDe(etat.motifs, familleDe(edition.saisie.genre))}
          genants={attelagesGenants(etat.affectations, edition.saisie)}
          envoi={envoi}
          quandChamp={quandChamp}
          quandEnregistrer={quandEnregistrer}
          quandFermer={quandFermer}
        />
      )}

      {edition.nom === 'motifs' && (
        <PanneauMotifs
          motifs={etat.motifs}
          envoi={envoi}
          quandCreer={quandCreerMotif}
          quandBasculer={quandBasculerMotif}
          quandFermer={quandFermer}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le formulaire d'un arrêt
// ---------------------------------------------------------------------------

function FormulaireArret({
  saisie,
  fautes,
  ressources,
  motifs,
  genants,
  envoi,
  quandChamp,
  quandEnregistrer,
  quandFermer,
}: {
  readonly saisie: SaisieArret;
  readonly fautes: Readonly<Record<string, string>>;
  readonly ressources: readonly LigneParc[];
  readonly motifs: readonly Motif[];
  readonly genants: readonly Affectation[];
  readonly envoi: boolean;
  readonly quandChamp: (champ: keyof SaisieArret, valeur: string) => void;
  readonly quandEnregistrer: () => void;
  readonly quandFermer: () => void;
}) {
  const parc = parcDe(saisie.genre);

  return (
    <aside className="panneau">
      <div className="panneau-haut">
        <div className="panneau-titre">
          <div>
            <div className="panneau-nom">Déclarer un arrêt</div>
          </div>
          <button className="fermer" type="button" onClick={quandFermer}>
            Fermer ✕
          </button>
        </div>
      </div>

      <div className="panneau-corps">
        <form
          className="formulaire"
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="champ-genre">
              Sur quoi
            </label>
            <select
              className="champ"
              id="champ-genre"
              value={saisie.genre}
              disabled={envoi}
              onChange={(e) => quandChamp('genre', e.target.value)}
            >
              {GENRES.map((g) => (
                <option value={g} key={g}>
                  {genreDit(g).charAt(0).toUpperCase() + genreDit(g).slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="champ-ressource">
              {genreDit(saisie.genre).charAt(0).toUpperCase() +
                genreDit(saisie.genre).slice(1)}
              <span className="sourdine"> · obligatoire</span>
            </label>
            <select
              className={fautes['ressourceId'] === undefined ? 'champ' : 'champ fautif'}
              id="champ-ressource"
              value={saisie.ressourceId}
              disabled={envoi}
              onChange={(e) => quandChamp('ressourceId', e.target.value)}
            >
              <option value="">—</option>
              {ressources
                .filter((r) => r.actif)
                .map((r) => (
                  <option value={r.id} key={r.id}>
                    {nommer(r, parc)}
                  </option>
                ))}
            </select>
            {fautes['ressourceId'] !== undefined && (
              <span className="erreur-champ">{fautes['ressourceId']}</span>
            )}
          </div>

          <div className="bloc-champ">
            <label className="etiquette" htmlFor="champ-motif">
              Motif<span className="sourdine"> · obligatoire</span>
            </label>
            <select
              className={fautes['motifId'] === undefined ? 'champ' : 'champ fautif'}
              id="champ-motif"
              value={saisie.motifId}
              disabled={envoi || motifs.length === 0}
              onChange={(e) => quandChamp('motifId', e.target.value)}
            >
              <option value="">—</option>
              {motifs.map((m) => (
                <option value={m.id} key={m.id}>
                  {m.nom}
                </option>
              ))}
            </select>
            {motifs.length === 0 && (
              <span className="erreur-champ">
                Aucun motif réglé pour{' '}
                {saisie.genre === 'chauffeur' ? 'le personnel' : 'le matériel'}.
              </span>
            )}
            {fautes['motifId'] !== undefined && (
              <span className="erreur-champ">{fautes['motifId']}</span>
            )}
          </div>

          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="champ-du">
              Du<span className="sourdine"> · obligatoire</span>
            </label>
            <input
              className={fautes['du'] === undefined ? 'champ' : 'champ fautif'}
              id="champ-du"
              type="date"
              value={saisie.du}
              disabled={envoi}
              onChange={(e) => quandChamp('du', e.target.value)}
            />
            {fautes['du'] !== undefined && (
              <span className="erreur-champ">{fautes['du']}</span>
            )}
          </div>

          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="champ-au">
              Au<span className="sourdine"> · si la fin est connue</span>
            </label>
            <input
              className={fautes['au'] === undefined ? 'champ' : 'champ fautif'}
              id="champ-au"
              type="date"
              value={saisie.au}
              disabled={envoi}
              onChange={(e) => quandChamp('au', e.target.value)}
            />
            {fautes['au'] !== undefined && (
              <span className="erreur-champ">{fautes['au']}</span>
            )}
          </div>

          <div className="bloc-champ">
            <label className="etiquette" htmlFor="champ-note">
              Note
            </label>
            <textarea
              className="champ"
              id="champ-note"
              rows={3}
              value={saisie.note}
              disabled={envoi}
              onChange={(e) => quandChamp('note', e.target.value)}
            />
          </div>

          {/* Un AVERTISSEMENT, pas un refus : la base accepte parfaitement
              d'immobiliser une remorque attelée, et c'est ce qu'on veut — une
              panne ne demande la permission à personne. Mais poser l'arrêt sans
              voir que le camion part demain avec serait une mauvaise surprise. */}
          {genants.length > 0 && (
            <p className="empechement">
              {saisie.genre === 'vehicule'
                ? 'Ce véhicule porte un attelage sur cette période :'
                : 'Cette ressource est attelée sur cette période :'}{' '}
              {genants.map((a) => attelageDit(a)).join(' ; ')}. L’arrêt sera quand même
              enregistré — pensez à détacher depuis l’écran Affectation.
            </p>
          )}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Le panneau des motifs
// ---------------------------------------------------------------------------

function PanneauMotifs({
  motifs,
  envoi,
  quandCreer,
  quandBasculer,
  quandFermer,
}: {
  readonly motifs: readonly Motif[];
  readonly envoi: boolean;
  readonly quandCreer: (nom: string, famille: Famille) => void;
  readonly quandBasculer: (id: string, actif: boolean) => void;
  readonly quandFermer: () => void;
}) {
  const [nom, setNom] = useState('');
  const [famille, setFamille] = useState<Famille>('personnel');

  const familles: readonly { readonly cle: Famille; readonly nom: string }[] = [
    { cle: 'personnel', nom: 'Personnel' },
    { cle: 'materiel', nom: 'Matériel' },
  ];

  return (
    <aside className="panneau">
      <div className="panneau-haut">
        <div className="panneau-titre">
          <div>
            <div className="panneau-nom">Motifs d’arrêt</div>
            <div className="panneau-code">propres à votre société</div>
          </div>
          <button className="fermer" type="button" onClick={quandFermer}>
            Fermer ✕
          </button>
        </div>
      </div>

      <div className="panneau-corps">
        <form
          className="formulaire"
          onSubmit={(e) => {
            e.preventDefault();
            if (nom.trim() === '') return;
            quandCreer(nom.trim(), famille);
            setNom('');
          }}
        >
          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="motif-nom">
              Nouveau motif
            </label>
            <input
              className="champ"
              id="motif-nom"
              value={nom}
              maxLength={80}
              disabled={envoi}
              onChange={(e) => setNom(e.target.value)}
            />
          </div>
          <div className="bloc-champ court">
            <label className="etiquette" htmlFor="motif-famille">
              S’applique à
            </label>
            <select
              className="champ"
              id="motif-famille"
              value={famille}
              disabled={envoi}
              onChange={(e) => setFamille(e.target.value as Famille)}
            >
              {familles.map((f) => (
                <option value={f.cle} key={f.cle}>
                  {f.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || nom.trim() === ''}
            >
              Ajouter
            </button>
          </div>
        </form>

        {/* La famille se pose une fois et ne se modifie plus : la faire passer
            du matériel au personnel rendrait fausses les indisponibilités déjà
            écrites avec ce motif. Un motif mal classé se désactive et se
            recrée — c'est dit ici parce que c'est ici qu'on se pose la
            question. */}
        <p className="empechement">
          Un motif ne se supprime pas : il resterait sur les arrêts passés, dont
          l’historique dirait « immobilisé pour rien ». On le désactive. Et sa famille
          ne se change pas — un motif mal classé se désactive et se recrée.
        </p>

        {familles.map((f) => {
          const siens = motifs.filter((m) => m.famille === f.cle);
          return (
            <div className="colonne-libres" key={f.cle}>
              <div className="colonne-titre">{f.nom}</div>
              {siens.length === 0 ? (
                <p className="empechement">Aucun motif.</p>
              ) : (
                siens.map((m) => (
                  <div
                    className={m.actif ? 'ligne-motif' : 'ligne-motif eteinte'}
                    key={m.id}
                  >
                    <span>{m.nom}</span>
                    <button
                      className="bouton bouton-discret"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandBasculer(m.id, !m.actif)}
                    >
                      {m.actif ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesAbsences {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranAbsences({ jeton, quandAccesPerdu }: ProprietesAbsences) {
  const [etat, setEtat] = useState<EtatBase>({ nom: 'chargement' });
  const [jour, setJour] = useState(aujourdhui());
  const [edition, setEdition] = useState<Edition>({ nom: 'aucune' });
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const [c, v, r, a, m, f] = await Promise.all([
      lireParc(j, PARC_CHAUFFEURS),
      lireParc(j, PARC_VEHICULES),
      lireParc(j, PARC_REMORQUES),
      lireIndisponibilites(j),
      lireMotifs(j),
      lireAffectations(j),
    ]);

    if (
      c.etat !== 'ok' ||
      v.etat !== 'ok' ||
      r.etat !== 'ok' ||
      a.etat !== 'ok' ||
      m.etat !== 'ok' ||
      f.etat !== 'ok'
    ) {
      for (const resultat of [c, v, r, a, m, f]) {
        if (resultat.etat === 'echec' && resultat.cause === 'acces') quandAccesPerdu();
      }
      setEtat({ nom: 'panne' });
      return;
    }

    setEtat({
      nom: 'pret',
      chauffeurs: c.valeur,
      vehicules: v.valeur,
      remorques: r.valeur,
      arrets: a.valeur,
      motifs: m.valeur,
      affectations: f.valeur,
    });
  }, [avecJeton, quandAccesPerdu]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    apres: () => void = () => {
      /* rien de plus */
    },
  ) => {
    setEnvoi(true);
    setRefus(null);

    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        apres();
        setEnvoi(false);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(REFUS[resultat.cause]);
      setEnvoi(false);
    })();
  };

  return (
    <VueAbsences
      etat={etat}
      jour={jour}
      quandJour={(j) => {
        if (j !== '') setJour(j);
      }}
      edition={edition}
      quandCreer={() =>
        setEdition({ nom: 'creation', saisie: saisieVide(jour), fautes: {} })
      }
      quandMotifs={() => setEdition({ nom: 'motifs' })}
      quandFermer={() => setEdition({ nom: 'aucune' })}
      quandChamp={(champ, valeur) =>
        setEdition((actuelle) => {
          if (actuelle.nom !== 'creation') return actuelle;
          const saisie = { ...actuelle.saisie, [champ]: valeur };
          // Changer de genre change la famille des motifs proposés : celui qui
          // était choisi ne s'applique peut-être plus, et le garder ferait
          // partir un motif du matériel sur un chauffeur.
          const nettoyee =
            champ === 'genre' ? { ...saisie, ressourceId: '', motifId: '' } : saisie;
          return { ...actuelle, saisie: nettoyee };
        })
      }
      quandEnregistrer={() => {
        if (edition.nom !== 'creation' || etat.nom !== 'pret') return;
        const fautes = validerArret(edition.saisie, etat.motifs);
        if (Object.keys(fautes).length > 0) {
          setEdition({ ...edition, fautes });
          return;
        }
        const corps = pourCreation(edition.saisie);
        ecrire(
          (j) => poserArret(j, corps),
          () => setEdition({ nom: 'aucune' }),
        );
      }}
      // Clore, c'est poser la fin la VEILLE du jour où l'on regarde — mêmes
      // bornes inclusives que pour un attelage.
      quandClore={(id) => ecrire((j) => changerArret(j, id, { au: veille(jour) }))}
      // `au` à `null` rouvre un arrêt clos par erreur : c'est un ordre valide.
      quandRouvrir={(id) => ecrire((j) => changerArret(j, id, { au: null }))}
      aSupprimer={aSupprimer}
      quandDemanderSuppression={setASupprimer}
      quandSupprimer={() => {
        if (aSupprimer === null) return;
        ecrire(
          (j) => supprimerArret(j, aSupprimer),
          () => setASupprimer(null),
        );
      }}
      quandRenoncer={() => setASupprimer(null)}
      quandCreerMotif={(nom, famille) => ecrire((j) => creerMotif(j, nom, famille))}
      quandBasculerMotif={(id, actif) => ecrire((j) => changerMotif(j, id, { actif }))}
      envoi={envoi}
      refus={refus}
    />
  );
}
